import {
  Deposit,
  DepositCondition,
  DepositConditionType,
  DepositDayType,
  DepositFromDateTimeUnit,
  FormCondition,
  FormConditionCondition,
  FormConditionConditionType,
  FormConditionDayType,
  FormConditionFromDateTimeUnit,
  StaffGetContractDatesResponse,
  useStaffGetContractDates,
} from '@urbanx/agx.campaigns.client'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { useCallback, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useBusinessDate } from 'hooks/useBusinessDate'

const getBaseDateFromConditionType = (
  conditionType: FormConditionConditionType | DepositConditionType | undefined,
  contractDates: StaffGetContractDatesResponse | undefined
): Dayjs | null => {
  if (!contractDates) return null

  let dateValue: Date | undefined

  switch (conditionType) {
    case FormConditionConditionType.Contract:
    case DepositConditionType.Contract:
      dateValue = contractDates.contractDate
      break
    case FormConditionConditionType.Finance:
    case DepositConditionType.Finance:
      dateValue = contractDates.subjectToFinance?.referenceDate
      break
    case FormConditionConditionType.Unconditional:
    case DepositConditionType.Unconditional:
      dateValue = contractDates.expectedUnconditionalDate
      break
  }

  if (!dateValue) return null

  const parsed = dayjs(dateValue)
  return parsed.isValid() ? parsed : null
}

// Helper to calculate a condition's due date from its own settings
const calculateConditionDueDate = (
  formCondition: FormCondition | undefined,
  contractDates: StaffGetContractDatesResponse | undefined,
  addTimeUnit: (
    baseDate: Dayjs,
    amount: number,
    unit: FormConditionFromDateTimeUnit | DepositFromDateTimeUnit | undefined,
    isBusinessDays: boolean
  ) => Dayjs
): Dayjs | null => {
  if (!formCondition || !contractDates) return null

  const conditionValue = formCondition.condition

  // If it's a specific date, use referenceDate directly
  if (conditionValue === FormConditionCondition.OnSpecificDate) {
    if (formCondition.referenceDate) {
      const parsed = dayjs(formCondition.referenceDate)
      return parsed.isValid() ? parsed : null
    }
    return null
  }

  // If it's N days from a date (contract date, etc.)
  if (conditionValue === FormConditionCondition.NdaysFromDate) {
    const baseDate = getBaseDateFromConditionType(
      formCondition.conditionType as FormConditionConditionType,
      contractDates
    )

    if (!baseDate) return null

    // If no days specified, use base date directly
    if (
      formCondition.daysFromDate === undefined ||
      formCondition.daysFromDate === 0
    ) {
      return baseDate
    }

    const isBusinessDays =
      formCondition.dayType === FormConditionDayType.BusinessDays

    return addTimeUnit(
      baseDate,
      formCondition.daysFromDate,
      formCondition.fromDateTimeUnit,
      isBusinessDays
    )
  }

  // Fallback to referenceDate if available
  if (formCondition.referenceDate) {
    const parsed = dayjs(formCondition.referenceDate)
    return parsed.isValid() ? parsed : null
  }

  return null
}

const getBaseDateFromCondition = (
  conditionValue: FormConditionCondition | DepositCondition | undefined,
  contractDates: StaffGetContractDatesResponse | undefined,
  addTimeUnit: (
    baseDate: Dayjs,
    amount: number,
    unit: FormConditionFromDateTimeUnit | DepositFromDateTimeUnit | undefined,
    isBusinessDays: boolean
  ) => Dayjs
): Dayjs | null => {
  if (!contractDates) return null

  let formCondition: FormCondition | undefined

  switch (conditionValue) {
    case FormConditionCondition.FinanceBeingSatisfied:
      formCondition = contractDates.subjectToFinance
      break
    case FormConditionCondition.BuildingAndPestBeingSatisfied:
      formCondition = contractDates.buildingAndPestCondition
      break
    case FormConditionCondition.UponSatisfactionOfSpecialCondition:
      // Special conditions might have their own reference date
      // This would need to be passed through the condition object
      return null
    default:
      return null
  }

  if (!formCondition) return null

  // Calculate the actual due date of the finance/building & pest condition
  return calculateConditionDueDate(formCondition, contractDates, addTimeUnit)
}

export const useFormConditionDueDate = (
  condition: FormCondition | Deposit | undefined,
  contractDateOverride?: Date
) => {
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const { moveToNextBusinessDay, addBusinessDays } = useBusinessDate(
    campaign?.state || ''
  )
  const {
    data: contractDates,
    isPending,
    isError,
  } = useStaffGetContractDates(
    contract?.contractId || '',
    !!contract?.contractId
  )

  const addTimeUnit = useCallback(
    (
      baseDate: Dayjs,
      amount: number,
      unit: FormConditionFromDateTimeUnit | DepositFromDateTimeUnit | undefined,
      isBusinessDays: boolean
    ): Dayjs => {
      // If business days and unit is Days or undefined (default to days), use addBusinessDays
      if (
        isBusinessDays &&
        (unit === FormConditionFromDateTimeUnit.Days || unit === undefined)
      ) {
        return addBusinessDays(baseDate, amount)
      }

      switch (unit) {
        case FormConditionFromDateTimeUnit.Days:
          return baseDate.add(amount, 'day')
        case FormConditionFromDateTimeUnit.Weeks:
          return baseDate.add(amount, 'week')
        case FormConditionFromDateTimeUnit.Months:
          return baseDate.add(amount, 'month')
        case FormConditionFromDateTimeUnit.Years:
          return baseDate.add(amount, 'year')
        default:
          return baseDate.add(amount, 'day')
      }
    },
    [addBusinessDays]
  )

  return useMemo(() => {
    if (isError || !condition) {
      return { dueDate: null, isPending, isError }
    }

    // If we don't have contract dates yet but have an override, use it
    const effectiveContractDates =
      contractDates ||
      (contractDateOverride
        ? {
            contractDate: contractDateOverride,
            subjectToFinance: undefined,
            buildingAndPestCondition: undefined,
          }
        : undefined)

    if (!effectiveContractDates && isPending) {
      return { dueDate: null, isPending, isError }
    }

    const conditionValue = condition.condition

    // Handle "OnDate" - use referenceDate or look up based on conditionType
    if (conditionValue === DepositCondition.OnDate) {
      const depositCondition = condition as Deposit
      let refDate: Dayjs | null = null

      // First try to use explicit referenceDate if provided
      if (condition.referenceDate) {
        const parsed = dayjs(condition.referenceDate)
        if (parsed.isValid()) {
          refDate = parsed
        }
      }

      // If no referenceDate, look up based on conditionType
      if (!refDate && depositCondition.conditionType !== undefined) {
        refDate = getBaseDateFromConditionType(
          depositCondition.conditionType,
          effectiveContractDates
        )
      }

      if (refDate) {
        // If this is "On Contract Date" and it falls on weekend, move to next business day
        if (
          depositCondition.conditionType === undefined ||
          depositCondition.conditionType === DepositConditionType.Contract
        ) {
          refDate = moveToNextBusinessDay(refDate)
        }
        return {
          dueDate: refDate.toDate(),
          isPending,
          isError,
        }
      }
    }

    // If it's a specific date, use referenceDate directly
    if (conditionValue === FormConditionCondition.OnSpecificDate) {
      if (condition.referenceDate) {
        const refDate = dayjs(condition.referenceDate)
        return {
          dueDate: refDate.isValid() ? refDate.toDate() : null,
          isPending,
          isError,
        }
      }
    }

    // Handle "FinanceBeingSatisfied" - use finance condition reference date
    if (conditionValue === FormConditionCondition.FinanceBeingSatisfied) {
      const baseDate = getBaseDateFromCondition(
        conditionValue,
        effectiveContractDates,
        addTimeUnit
      )
      if (baseDate) {
        // If there are additional days to add
        if (
          condition.daysFromDate !== undefined &&
          condition.daysFromDate > 0
        ) {
          const calculatedDate = addTimeUnit(
            baseDate,
            condition.daysFromDate,
            condition.fromDateTimeUnit,
            condition.dayType === FormConditionDayType.BusinessDays
          )
          return {
            dueDate: moveToNextBusinessDay(calculatedDate).toDate(),
            isPending,
            isError,
          }
        }
        return {
          dueDate: moveToNextBusinessDay(baseDate).toDate(),
          isPending,
          isError,
        }
      }
    }

    // Handle "BuildingAndPestBeingSatisfied" - use building and pest condition reference date
    if (
      conditionValue === FormConditionCondition.BuildingAndPestBeingSatisfied
    ) {
      const baseDate = getBaseDateFromCondition(
        conditionValue,
        effectiveContractDates,
        addTimeUnit
      )
      if (baseDate) {
        // If there are additional days to add
        if (
          condition.daysFromDate !== undefined &&
          condition.daysFromDate > 0
        ) {
          const calculatedDate = addTimeUnit(
            baseDate,
            condition.daysFromDate,
            condition.fromDateTimeUnit,
            condition.dayType === FormConditionDayType.BusinessDays
          )
          return {
            dueDate: moveToNextBusinessDay(calculatedDate).toDate(),
            isPending,
            isError,
          }
        }
        return {
          dueDate: moveToNextBusinessDay(baseDate).toDate(),
          isPending,
          isError,
        }
      }
    }

    // Handle "UponSatisfactionOfSpecialCondition"
    if (
      conditionValue ===
      FormConditionCondition.UponSatisfactionOfSpecialCondition
    ) {
      // If the condition has a referenceDate (pre-computed or manually set), use it
      if (condition.referenceDate) {
        const baseDate = dayjs(condition.referenceDate)
        if (baseDate.isValid()) {
          // If there are additional days to add
          if (
            condition.daysFromDate !== undefined &&
            condition.daysFromDate > 0
          ) {
            const calculatedDate = addTimeUnit(
              baseDate,
              condition.daysFromDate,
              condition.fromDateTimeUnit,
              condition.dayType === FormConditionDayType.BusinessDays
            )
            return {
              dueDate: moveToNextBusinessDay(calculatedDate).toDate(),
              isPending,
              isError,
            }
          }
          return {
            dueDate: moveToNextBusinessDay(baseDate).toDate(),
            isPending,
            isError,
          }
        }
      }
    }

    // If it's N days from a date (or AsPerContract which is equivalent)
    if (
      conditionValue === FormConditionCondition.NdaysFromDate ||
      conditionValue === DepositCondition.NdaysFromDate ||
      conditionValue === FormConditionCondition.AsPerContract
    ) {
      const baseDate = getBaseDateFromConditionType(
        condition.conditionType as
          | FormConditionConditionType
          | DepositConditionType,
        effectiveContractDates
      )

      if (!baseDate) {
        return { dueDate: null, isPending, isError }
      }

      // If daysFromDate is undefined or 0, use base date directly
      if (
        condition.daysFromDate === undefined ||
        condition.daysFromDate === 0
      ) {
        return {
          dueDate: moveToNextBusinessDay(baseDate).toDate(),
          isPending,
          isError,
        }
      }

      const isBusinessDays =
        condition.dayType === FormConditionDayType.BusinessDays ||
        condition.dayType === DepositDayType.BusinessDays

      const calculatedDate = addTimeUnit(
        baseDate,
        condition.daysFromDate,
        condition.fromDateTimeUnit,
        isBusinessDays
      )

      return {
        dueDate: moveToNextBusinessDay(calculatedDate).toDate(),
        isPending,
        isError,
      }
    }

    // Handle deposits with conditionType but no condition value set
    // This covers cases where a deposit only has conditionType (e.g., "Contract") without an explicit condition
    if (conditionValue === undefined && condition.conditionType !== undefined) {
      const depositCondition = condition as Deposit
      const baseDate = getBaseDateFromConditionType(
        depositCondition.conditionType,
        effectiveContractDates
      )

      if (baseDate) {
        // Move to next business day if it falls on weekend or public holiday
        return {
          dueDate: moveToNextBusinessDay(baseDate).toDate(),
          isPending,
          isError,
        }
      }
    }

    return { dueDate: null, isPending, isError }
  }, [condition, isPending, isError, contractDates, contractDateOverride])
}
